import {FieldValue} from 'firebase/firestore';

/**
 * Flattens a nested object into dot notation for Firestore updates.
 * @param obj - The object to flatten.
 * @param prefix - (Internal) Used for recursion to build the dot path.
 * @returns A flattened object suitable for updateDoc.
 */
export function toDotNotation(obj: Record<string, unknown>, prefix = ''): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  Object.keys(obj).forEach((key) => {
    const value = obj[key];
    const newKey = prefix ? `${prefix}.${key}` : key;

    // We only recurse if the value is:
    // 1. A non-null object
    // 2. NOT an Array (we want to replace arrays wholesale)
    // 3. NOT a native Date object
    // 4. NOT a Firestore FieldValue (e.g. serverTimestamp, deleteField)
    if (
      value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      !(value instanceof Date) &&
      !(value instanceof FieldValue) &&
      Object.keys(value).length > 0
    ) {
      // Recurse
      const nested = toDotNotation(value as Record<string, unknown>, newKey);
      Object.assign(result, nested);
    } else {
      // It's a leaf value (primitive, array, date, FieldValue, or empty object)
      result[newKey] = value;
    }
  });

  return result;
}
